import * as Actions from '../actions/identificationActions';
import Identification from '../../entities/identification';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {getSessionFirmId, getSessionUserId} from '../../lib/picos/sessionIdentity';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type IdentificationAction = {
    type: string;
    identification: Identification | null; 
    identificationId: string | Identification; 
    partial: boolean; 
    lastFetch: number; 
    identifications: Identification[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<Identification> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {Identification[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<Identification> | null, firmId: string, userId: string, updates?: Identification[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'identification', 'identifications', data ? backupRows(data) : null, updates)
		  .then(result => {
			  // OWA-786 (CXR-4-001): the worker resolves a typed result for every
			  // path — a resolution is NOT an acknowledgement. Only ok: true means
			  // the backup write is durable; anything else is surfaced, never
			  // swallowed, so a skipped or failed write cannot masquerade as one.
			  if (!result?.ok) console.warn(`⮂ identificationReducer: backup write for 'identifications' did not persist (${result?.reason ?? 'unknown'}): ${result?.message ?? ''}`);
		  })
		  .catch(console.error);
}

/**
 * The identity that names this tab's backup store, or null when this tab has
 * not finished authenticating.
 *
 * OWA-785: this must come from sessionStorage (per tab), never from
 * localStorage (shared by every tab on the origin). With two firms open in two
 * tabs, the localStorage value is whichever firm logged in last, so the other
 * tab persisted its entities into that firm's backup store and they restored
 * into the wrong firm's UI. A partial identity names some other store, so we
 * skip the write rather than misfile it.
 *
 * @returns {{firmId: string, userId: string} | null}
 */
function storageIdentity(): {firmId: string; userId: string} | null {
	const firmId = getSessionFirmId();
	const userId = getSessionUserId();

	if (!firmId || !userId) {
		if (logging) console.debug('⮂ identificationReducer: no session identity, skipping backup write');
		return null;
	}

	return {firmId, userId};
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<Identification>} data
 * @param {Identification[]} [updates]
 * @returns {ArrayMap<Identification>}
 */
function updateStorage(data: ArrayMap<Identification>, updates?: Identification[]): ArrayMap<Identification> {
	if (logging) console.debug('⮂ identificationReducer: updateStorage');

	if (updates && updates.length === 0) return data;

	const identity = storageIdentity();
	if (!identity) return data;

	if (updates) callStorageWorker(null, identity.firmId, identity.userId, updates);
	else callStorageWorker(data, identity.firmId, identity.userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {Identification[]} [updates]
 * @returns {ArrayMap<Identification>}
 */
function updateStorageNoReturn(updates?: Identification[]): void {
	if (logging) console.debug('⮂ identificationReducer: updateStorageNoReturn');

	if (!updates || updates.length === 0) return;

	const identity = storageIdentity();
	if (!identity) return;

	callStorageWorker(null, identity.firmId, identity.userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {Identification[]} [data]
 * @returns {ArrayMap<Identification>}
 */
function createState(data?: Identification[] | ArrayMap<Identification>): ArrayMap<Identification> {
	if (!data) return ArrayMapRegistry.clone<Identification>('identification', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<Identification>('identification', data.map(e => e));
    else return ArrayMapRegistry.clone<Identification>('identification', data);
}

// Whether deleted identifications keep their rows (schema includeDeletedInResults). OWA-68
const retainDeleted = false;

/**
 * The full backup snapshot of a collection: one entry per ID. A row when there is one (deleted rows of a
 * type that keeps them included); otherwise a deletion record for an ID whose row was removed. OWA-68
 *
 * @param {ArrayMap<Identification>} data
 * @returns {Array<Identification | IdentificationDeletionRecord>}
 */
function backupRows(data: ArrayMap<Identification>): Array<Identification | IdentificationDeletionRecord> {
	const records: IdentificationDeletionRecord[] = data.deletedVersionEntries()
		.filter(([id]) => !data.has(id))
		.map(([id, addedOn]) => ({identificationId: id, addedOn, status: 'D', deletionRecord: true}));

	return records.length ? [...data.values(), ...records] : data.values();
}

/**
 * A deletion record read back from the offline backup: evidence that a identification was deleted at a
 * version, never a live row. The storage worker writes one for a deleted identification whose type does
 * not keep deleted rows. OWA-68
 */
type IdentificationDeletionRecord = {identificationId: string; addedOn: number; status: 'D'; deletionRecord: true};

const isDeletionRecord = (o: unknown): o is IdentificationDeletionRecord => (o as IdentificationDeletionRecord)?.deletionRecord === true;

/**
 * Apply one incoming version of a identification to a collection the caller owns (a clone), under the one
 * acceptance rule every path uses (OWA-68):
 * - older than the stored row: rejected
 * - an active version equal to a kept deleted row: rejected (it yields only to a strictly newer one)
 * - no stored row, and at or below the recorded deletion version: rejected
 * - an accepted deleted version records its deletion version; the row is kept as a deleted row when
 *   this type keeps deleted rows, otherwise it is removed
 * - an accepted active version is stored (a newer one supersedes the deletion version)
 *
 * @param {ArrayMap<Identification>} st
 * @param {Identification | IdentificationDeletionRecord} incoming
 * @returns {boolean} whether the version was accepted; only accepted versions go to the backup
 */
function applyVersion(st: ArrayMap<Identification>, incoming: Identification | IdentificationDeletionRecord): boolean {
	const id = isDeletionRecord(incoming) ? incoming.identificationId : incoming.id;
	const current = st.get(id);

	if (current) {
		if (incoming.addedOn < current.addedOn) return false;
		// A kept deleted row yields only to a strictly newer active version, like a deletion version
		if (current.status === 'D' && incoming.status !== 'D' && incoming.addedOn === current.addedOn) return false;
	}
	else {
		const deletedAt = st.deletedVersion(id);
		if (deletedAt !== undefined && incoming.addedOn <= deletedAt) return false;
	}

	if (incoming.status === 'D') {
		st.recordDeletion(id, incoming.addedOn);
		if (retainDeleted && !isDeletionRecord(incoming)) st.set(incoming);
		else if (current) st.delete(current);
	}
	else st.set(incoming as Identification);

	return true;
}

/**
 * A new indexed collection holding the rows and deletion versions of the given one. Fetch paths build
 * from this instead of cloning, because the initial store state is a bare ArrayMap without the
 * schema's indexes. OWA-68
 *
 * @param {ArrayMap<Identification>} from
 * @returns {ArrayMap<Identification>}
 */
function carryOver(from: ArrayMap<Identification>): ArrayMap<Identification> {
	const next = createState(from);
	from.deletedVersionEntries().forEach(([id, version]) => next.recordDeletion(id, version));
	return next;
}

function identifications(state: ArrayMap<Identification>, action: IdentificationAction): ArrayMap<Identification> {
	if (!state) state = createState();

	let st: ArrayMap<Identification>;

	switch (action.type) {
		// action.identification - the new value of identification with id action.id
		// action.identificationId - id of the target, used if new value is null
		case Actions.IDENTIFICATION_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ identificationReducer: FETCH_ONE_SUCCESS', action);

			if (action.identification === null) return state;

			// Only for a identification this collection knows (a row, or a deletion version), and only when the
			// fetched version is accepted (OWA-68).
			if (state.has(action.identificationId) || state.deletedVersion(action.identification.id) !== undefined) {
				st = state.clone();
				if (!applyVersion(st, action.identification)) return state;

				updateStorageNoReturn([action.identification]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.identifications
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.identifications
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.identifications
			 !partial, !stateEmpty, fetchEmpty	-> D return identifications in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge identifications in state newer than those in action.identifications
		 */
		case Actions.IDENTIFICATION_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.identifications.length === 0;
			// A deletion record from the backup is evidence only: never constructed into an entity (OWA-68)
			const fetched: Array<Identification | IdentificationDeletionRecord> = action.identifications.map(identification => isDeletionRecord(identification) ? identification : new Identification({...identification, persisted: true}));
			const fetchedIds = new Set(fetched.map(identification => isDeletionRecord(identification) ? identification.identificationId : identification.id));

			// A full fetch is the whole dataset, so a persisted active row it omits is dropped (deleted rows,
			// deletion versions and unsaved local rows stay). OWA-68
			const dropOmitted = (st: ArrayMap<Identification>) => {
				[...st.values()].forEach(row => {
					if (!fetchedIds.has(row.id) && row.persisted && row.status !== 'D') st.delete(row);
				});
			};

			if (logging) console.debug(`⮂ identificationReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} identifications with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ identificationReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Build a new indexed state that keeps any deletion versions (the visible state is
					// empty, but a deletion version can still reject an older row). OWA-68
					st = carryOver(state);
					fetched.forEach(identification => applyVersion(st, identification));

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ identificationReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<Identification> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: Identification[] = [];

				// Partials include deleted rows for every entity; applyVersion keeps or removes them and
				// records their deletion version (OWA-68). Only accepted versions go to the backup.
				fetched.forEach(identification => {
					if (applyVersion(st, identification)) toUpdate.push(identification as Identification);
					else if (logging) console.debug('⮂ identificationReducer: partialMerge: rejected older version of identification');
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ identificationReducer: empty: identifications', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Drop the persisted active rows; deleted rows, deletion versions and unsaved local rows
				// stay (OWA-68)
				st = carryOver(state);
				dropOmitted(st);

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ identificationReducer: fullMerge: identifications', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Start from the current rows and deletion versions, apply each fetched version under the one
				// rule, then drop persisted active rows the full fetch omitted (OWA-68). A fetched row replaces
				// a local one unless the local one is strictly newer, so a stale local (persisted or not) never
				// shadows a newer server row (OWA-7 #1), and a newer row that arrived while the fetch was in
				// flight (for example a socket update during startup restore) is kept.
				st = carryOver(state);
				fetched.forEach(identification => applyVersion(st, identification));
				dropOmitted(st);

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.IDENTIFICATION_FETCH_ONE_FAILURE:
			console.error('IDENTIFICATION_FETCH_ONE_FAILURE', action.identificationId, action.err);

			// No update to storage required
			return state;

		case Actions.IDENTIFICATION_FETCH_FAILURE:
			console.error('IDENTIFICATION_FETCH_FAILURE', action.identificationId, action.err);

			// No update to storage required
			return state;

		case Actions.IDENTIFICATION_RESTORE: {
		    let identification = new Identification({...action.identification});
            st = state.clone();

            // One acceptance rule for every incoming version (OWA-68)
            if (!applyVersion(st, identification)) return state;

            return updateStorage(st, [identification]);
        }
            
		case Actions.IDENTIFICATION_SAVE: {
		    // console.debug('⮂ IDENTIFICATION_SAVE reducer', action.identification?.id);
		    
			// clone the identification
			let identification = new Identification({...action.identification});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new identification in the state, under the one acceptance rule (OWA-68). A rejected
			// version changes nothing and is not written to the backup.
			if (!applyVersion(st, identification)) return state;

			return updateStorage(st, [identification]);
		}

		case Actions.IDENTIFICATION_SAVE_MANY: {
		    if (logging) console.debug('⮂ IDENTIFICATION_SAVE_MANY reducer', action.identifications.map(identification => identification.id).join(','));
		    
			// clone the identifications
			let toProcess = action.identifications.map(identification => new Identification(identification));

			st = state.clone();
			// state.clear();

			const updates: Identification[] = [];

			toProcess.forEach(identification => {
				// Add or update the new identification in the state, under the one acceptance rule (OWA-68)
				if (applyVersion(st, identification)) updates.push(identification);
				else if (logging) console.debug('⮂ IDENTIFICATION_SAVE_MANY: rejected older version of identification');
			});

			return updateStorage(st, updates);
		}

		case Actions.IDENTIFICATION_REMOVE: {
			// clone the identification; a remove is a deleted version (the remove saga stamps it 'D' and a new
			// addedOn)
			const identification = new Identification({...action.identification, status: 'D'});

			// Remove from UI under the one acceptance rule: the deletion version is recorded, and the row
			// is removed, or kept as a deleted row for a type that keeps them (OWA-68)
			st = state.clone();
			if (!applyVersion(st, identification)) return state;

			return updateStorage(st, [identification]);
		}

		case Actions.IDENTIFICATION_RESTORE_FAILURE:
		case Actions.IDENTIFICATION_SAVE_FAILURE: {
		    console.warn('IDENTIFICATION_SAVE_FAILURE reducer', action.identification?.id);
		    if (!action.identification?.id) {
				console.warn('IDENTIFICATION_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const identification = ensure(Identification, action.identification);
			if (!identification) {
				console.error('Could not find identification after call to save');
				return state;
			}

			st = state.clone();

			const _identificationRef = state.get(identification.id);

			if (_identificationRef && (_identificationRef.addedOn === identification.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _identification = new Identification({..._identificationRef});
				_identification.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_identification.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.update('identification', identification.addedOn, {fatalError: _identification.fatalError}).then(r => {
						if (r.isErr()) console.error(`⮂ identificationReducer: failed to save identification with fatalError info: ${r.unwrapErr().message}`);
						else if (r.unwrap() === 0) {
							// Non-document 4xx handlers may remove the row before dispatch.
							// Preserve the historical rejected-operation queue behavior.
							idb.add('identification', _identification).then(saved => {
								if (saved.isErr()) console.error('Could not restore rejected identification: ' + saved.unwrapErr().message);
							}).catch(err => console.error('Could not restore rejected identification: ' + err.message));
						}
						else console.debug('⮂ identificationReducer: saved identification with fatalError info');
					}).catch(err => console.error(`⮂ identificationReducer: failed to save identification with fatalError info: ${err.message}`));

				st.set(_identification);
			}

			return updateStorage(st);
		}

		case Actions.IDENTIFICATION_SAVE_MANY_FAILURE: {
		    console.warn('IDENTIFICATION_SAVE_MANY_FAILURE reducer', action.identifications.map(identification => identification.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.IDENTIFICATION_RESTORE_SUCCESS:
		case Actions.IDENTIFICATION_SAVE_SUCCESS: {
			if (!action.identification?.id) {
				console.warn('IDENTIFICATION_SAVE_SUCCESS reducer');
                return state;
			}

			const identificationRef = state.get(action.identification?.id);
			if (!identificationRef) {
				console.warn('Could not find identification after call to save');
                return state;
			}

			st = state.clone();

			// The echoed version goes through the one acceptance rule (OWA-68). When it is older than the
			// current row, only the current row's persisted flag changes, and the current row (never the
			// rejected echo) is what the backup receives.
			if (applyVersion(st, action.identification)) return updateStorage(st, [action.identification]);

			if (identificationRef.persisted) return state;

			// Create a new instance to avoid mutating the entity shared with old state
			const identification = new Identification({...identificationRef, persisted: true});
			st.set(identification);

			return updateStorage(st, [identification]);
		}

		case Actions.IDENTIFICATION_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ IDENTIFICATION_SAVE_MANY_SUCCESS reducer', action.identifications.map(identification => identification.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const identifications: Identification[] = [];

			action.identifications.forEach(e => {
				const identificationRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!identificationRef) console.warn('Could not find identification after call to save');
				// The echoed version goes through the one acceptance rule (OWA-68)
				else if (applyVersion(st, e)) identifications.push(e);
				else if (!identificationRef.persisted) {
					// Older echo: only the current row's persisted flag changes, and the current row is
					// what the backup receives. Create a new instance to avoid mutating the entity shared
					// with old state.
					const identification = new Identification({...identificationRef, persisted: true});
					st.set(identification);
					identifications.push(identification);
				}
			});

			return updateStorage(st, identifications);
		}

		case Actions.IDENTIFICATION_REMOVE_FAILURE: {
		    console.warn('IDENTIFICATION_REMOVE_FAILURE action', action.identification?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.IDENTIFICATION_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.IDENTIFICATION_RESTORE_MANY: {
			if (logging) console.debug('⮂ IDENTIFICATION_RESTORE_MANY reducer', action.identifications.map(identification => identification.id).join(','));

			const toProcess = action.identifications.map(identification => new Identification({...identification}));
			st = state.clone();

			const updates: Identification[] = [];

			// One acceptance rule for every incoming version (OWA-68): a deleted version removes the row and
			// records its deletion version, or keeps a deleted row for a type that keeps them.
			toProcess.forEach(identification => {
				if (applyVersion(st, identification)) updates.push(identification);
				else if (logging) console.debug('⮂ IDENTIFICATION_RESTORE_MANY: rejected older version of identification');
			});

			return updateStorage(st, updates);
		}

		case Actions.IDENTIFICATION_REMOVE_MANY: {
			if (logging) console.debug('⮂ IDENTIFICATION_REMOVE_MANY reducer', action.identifications.map(identification => identification.id).join(','));

			// A socket delete is a deleted version, under the one acceptance rule (OWA-68)
			const toProcess = action.identifications.map(identification => new Identification({...identification, status: 'D'}));
			st = state.clone();

			const updates: Identification[] = [];

			toProcess.forEach(identification => {
				if (applyVersion(st, identification)) updates.push(identification);
				else if (logging) console.debug('⮂ IDENTIFICATION_REMOVE_MANY: rejected older version of identification');
			});

			return updateStorage(st, updates);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default identifications;